import { RecordCollection } from './record-collection.js';
import {
  Button,
  Combobox,
  DateRangePicker,
  formatCivilRange,
  MultiSelect,
  IconButton,
  todayCivilDate,
  useSnackbar,
  type ComboboxChoice,
} from '@hue/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, Download, ScrollText, Eye } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  auditApi,
  getApiError,
  type AuditEntry,
  type AuditOriginFilter,
  type AuditSort,
} from '../lib/api.js';
import { formatDateTime } from '../lib/time-zone.js';
import { auditTone, formatAuditValue } from './audit-format.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  SortBar,
} from './data-view.js';
import type { AuditView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { PageHeader } from './page-title.js';

/// Quem não tem cadastro aqui não tem id: o filtro carrega o e-mail que o sistema anterior
/// guardava — ou o nome, quando nem isso existe —, marcado para não se confundir com um id.
const BY_EMAIL = 'email:';
const BY_NAME = 'nome:';

const originOptions: Array<{ value: AuditOriginFilter; label: string }> = [
  { value: 'ALL', label: 'Todas' },
  { value: 'USER', label: 'Empresa' },
  { value: 'ROOT', label: 'Suporte' },
  { value: 'SYSTEM', label: 'Sistema' },
];

const originLabels: Record<string, string> = {
  USER: 'Pessoa da empresa',
  ROOT: 'Suporte da plataforma',
  SYSTEM: 'Sistema',
  WEBHOOK: 'Integração',
  MIGRATION: 'Migração',
};

const sortColumns: Array<{ sort: AuditSort; label: string }> = [
  { sort: 'createdAt', label: 'Quando' },
  { sort: 'actor', label: 'Pessoa' },
  { sort: 'action', label: 'Ação' },
];

const toneDots: Record<string, string> = {
  positive: 'bg-positive',
  progress: 'bg-info',
  danger: 'bg-danger',
  neutral: 'bg-content/30',
};

export function CompanyAudit({
  timeZone,
  canExport,
  view,
  onView,
}: {
  timeZone: string;
  canExport: boolean;
  view: AuditView;
  onView: ViewSetter<AuditView>;
}) {
  const snackbar = useSnackbar();
  const { search, action, actor, entityTypes, origin, from, to, sort, direction, limit, offset } =
    view;
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  function toggleDetails(entry: AuditEntry) {
    setExpanded((current) =>
      current.includes(entry.id) ? current.filter((id) => id !== entry.id) : [...current, entry.id],
    );
  }

  const filters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(action === 'ALL' ? {} : { action }),
    ...(actor === 'ALL'
      ? {}
      : actor.startsWith(BY_EMAIL)
        ? { actorEmail: actor.slice(BY_EMAIL.length) }
        : actor.startsWith(BY_NAME)
          ? { actorName: actor.slice(BY_NAME.length) }
          : { actorUserId: actor }),
    ...(entityTypes ? { entityTypes } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    origin,
    sort,
    direction,
    limit,
    offset,
  };
  const events = useQuery({
    queryKey: ['company', 'audit', filters],
    queryFn: () => auditApi.events(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  function toggleSort(column: AuditSort) {
    if (sort === column) onView({ direction: direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  /// O botão fica no alto e a lista é longa: quem manda exportar costuma continuar rolando
  /// enquanto o arquivo é montado. Por isso o resultado vai para o aviso flutuante — uma faixa
  /// presa ao topo responderia a uma parte da tela que já não está mais sendo olhada.
  async function exportCsv() {
    setExporting(true);
    try {
      const blob = await auditApi.exportCsv(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'auditoria.csv';
      link.click();
      URL.revokeObjectURL(url);
      // O download em si não avisa nada em celular: o arquivo cai na pasta e a tela não muda.
      snackbar.success('Auditoria exportada para auditoria.csv.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setExporting(false);
    }
  }

  const total = events.data?.total ?? 0;
  const entries = events.data?.entries ?? [];

  const actionOptions = [
    { value: 'ALL', label: 'Todas as ações' },
    ...(events.data?.actions ?? []).map((entry) => ({ value: entry.action, label: entry.label })),
  ];
  /// Duas contas podem ter o mesmo nome — a pessoa na empresa e a mesma pessoa no suporte da
  /// plataforma são cadastros diferentes. Quando o nome repete, o e-mail entra junto: sem isso a
  /// lista mostra duas linhas idênticas e não há como saber qual é qual. Quem veio do sistema
  /// anterior e ainda não tem cadastro aqui aparece do mesmo jeito, recortada pelo nome.
  const actorOptions = useMemo(() => {
    const people = events.data?.actors ?? [];
    const repeated = new Set(
      people
        .filter((p, at) => people.some((other, i) => i !== at && other.name === p.name))
        .map((p) => p.name),
    );
    return [
      { value: 'ALL', label: 'Todas as pessoas' },
      ...people.map((person) => ({
        value:
          person.id ?? (person.email ? `${BY_EMAIL}${person.email}` : `${BY_NAME}${person.name}`),
        label: repeated.has(person.name)
          ? `${person.name} · ${person.email ?? 'sem cadastro aqui'}`
          : person.name,
      })),
    ];
  }, [events.data?.actors]);
  const entityOptions = (events.data?.entities ?? []).map((entity) => ({
    value: entity.entityType,
    label: entity.label,
  }));
  /// O que está de fato marcado: o recorte da pessoa ou, sem recorte, todas as telas conhecidas.
  const markedEntities = entityTypes ?? entityOptions.map((option) => option.value);

  function label(options: Array<{ value: string; label: string }>, value: string): string {
    return options.find((option) => option.value === value)?.label ?? '';
  }

  const activeFilters = [
    ...(action === 'ALL'
      ? []
      : [
          {
            key: 'action',
            label: label(actionOptions, action),
            onRemove: () => onView({ action: 'ALL' }),
          },
        ]),
    ...(actor === 'ALL'
      ? []
      : [
          {
            key: 'actor',
            label: label(actorOptions, actor),
            onRemove: () => onView({ actor: 'ALL' }),
          },
        ]),
    ...(entityTypes === undefined
      ? []
      : [
          {
            key: 'entities',
            label:
              entityTypes.length === 1
                ? label(entityOptions, entityTypes[0]!)
                : `${String(entityTypes.length)} telas`,
            onRemove: () => onView({ entityTypes: undefined }),
          },
        ]),
    ...(origin === 'ALL'
      ? []
      : [
          {
            key: 'origin',
            label: label(originOptions, origin),
            onRemove: () => onView({ origin: 'ALL' }),
          },
        ]),
    ...((from ?? to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from, to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  function clearFilters() {
    onView({
      action: 'ALL',
      actor: 'ALL',
      entityTypes: undefined,
      origin: 'ALL',
      from: null,
      to: null,
    });
  }

  return (
    <section aria-labelledby="company-audit-title">
      <PageHeader
        trail="Configurações / Auditoria"
        id="company-audit-title"
        title="Auditoria"
        description="Cada alteração feita na empresa: quem fez, em que tela, quando — e, nas edições, o valor que havia antes ao lado do que ficou."
        actions={
          <>
            {canExport ? (
              <Button variant="outline" loading={exporting} onClick={() => void exportCsv()}>
                <Download className="size-4" />
                Exportar CSV
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBar
        searchId="audit-search"
        searchLabel="Buscar registro"
        searchPlaceholder="Detalhe, nome ou e-mail"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={clearFilters}
        count={`${String(total)} ${total === 1 ? 'registro' : 'registros'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'registro' : 'registros'}`}
        pageSize={limit}
        onPageSizeChange={(size) => onView({ limit: size })}
      >
        <MultiSelect
          label="Telas"
          className="lg:w-52"
          options={entityOptions}
          selected={markedEntities}
          onChange={(values) => onView({ entityTypes: values })}
          itemNoun="telas"
          searchPlaceholder="Buscar tela"
        />
        <Combobox
          label="Quem fez"
          placeholder="Todas as pessoas"
          className="lg:w-52"
          selectedLabel={actor === 'ALL' ? '' : label(actorOptions, actor)}
          options={actorOptions}
          onSelect={(choice: ComboboxChoice) =>
            choice.kind === 'option' ? onView({ actor: choice.value }) : undefined
          }
          emptyLabel="Ninguém com esse nome na trilha"
        />
        <Combobox
          label="Ação"
          placeholder="Todas as ações"
          className="lg:w-56"
          selectedLabel={action === 'ALL' ? '' : label(actionOptions, action)}
          options={actionOptions}
          onSelect={(choice: ComboboxChoice) =>
            choice.kind === 'option' ? onView({ action: choice.value }) : undefined
          }
          emptyLabel="Nenhuma ação com esse nome"
        />
        <FilterGroup
          label="Origem"
          description="Filtrar a trilha por origem"
          options={originOptions}
          value={origin}
          onChange={(value) => onView({ origin: value })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período da trilha"
            value={{ from, to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {events.isLoading ? (
        <LoadingCard label="Carregando auditoria" />
      ) : events.error ? (
        <ErrorCard message={getApiError(events.error)} />
      ) : entries.length ? (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <SortBar
            columns={sortColumns}
            sort={sort}
            direction={direction}
            onToggle={toggleSort}
            label="Ordenar os registros"
          />
          <RecordCollection
            label="Auditoria"
            records={entries}
            recordKey={(entry) => entry.id}
            sorting={{
              sort,
              direction,
              columns: sortColumns.map((item) => item.sort),
              onToggle: (key) => {
                const column = sortColumns.find((item) => item.sort === key);
                if (column) toggleSort(column.sort);
              },
            }}
            columns={[
              {
                key: 'createdAt',
                label: 'Quando',
                render: (entry) => (
                  <span className="text-xs text-content-muted">
                    {formatDateTime(entry.occurredAt, timeZone)}
                  </span>
                ),
              },
              {
                key: 'actor',
                label: 'Quem fez',
                render: (entry) => <strong>{entry.actorName}</strong>,
              },
              {
                key: 'action',
                label: 'Ação',
                render: (entry) => (
                  <span>
                    <span>{entry.label.toLowerCase()}</span>{' '}
                    <span>em {entry.entityLabel.toLowerCase()}</span>
                  </span>
                ),
              },
              {
                key: 'summary',
                label: 'O que mudou',
                render: (entry) => (
                  <div className="text-xs">
                    {entry.group ? (
                      <>
                        <button
                          type="button"
                          className="text-left font-semibold hover:underline"
                          aria-expanded={expanded.includes(entry.id)}
                          onClick={() => toggleDetails(entry)}
                        >
                          {entry.group.title}
                        </button>
                        <p>{entry.group.explanation}</p>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="text-left hover:underline"
                        aria-expanded={expanded.includes(entry.id)}
                        onClick={() => toggleDetails(entry)}
                      >
                        {entry.summary}
                      </button>
                    )}
                    {!entry.group && entry.changes.length ? (
                      <Changes entry={entry} className="mt-1" />
                    ) : null}
                  </div>
                ),
              },
            ]}
            actions={(entry) => (
              <IconButton
                label={expanded.includes(entry.id) ? 'Ocultar detalhes' : 'Ver detalhes'}
                icon={<Eye />}
                aria-expanded={expanded.includes(entry.id)}
                onClick={() => toggleDetails(entry)}
              />
            )}
            details={(entry) =>
              expanded.includes(entry.id) ? <AuditDetails entry={entry} /> : null
            }
            card={(entry) => (
              <ol>
                <AuditEvent entry={entry} timeZone={timeZone} />
              </ol>
            )}
          />
          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onOffsetChange={(next) => onView({ offset: next })}
            itemLabel={(count) => (count === 1 ? 'registro' : 'registros')}
          />
        </div>
      ) : (
        <EmptyCard
          icon={ScrollText}
          title="Nenhum registro encontrado"
          description={
            markedEntities.length === 0
              ? 'Nenhuma tela marcada no filtro. Marque ao menos uma para ver os eventos.'
              : 'Ajuste a busca, a tela, quem fez, a ação ou o período consultado.'
          }
        />
      )}
    </section>
  );
}

/// Um evento da trilha. O que mudou aparece direto na linha — é o que a pessoa veio ver; o detalhe
/// técnico (registro, requisição, IP) fica atrás de um clique, porque só interessa quando algo
/// precisa ser rastreado até a origem.
///
/// Dois registros da mesma operação — a cobrança online encerrada e o pagamento que ela cancelou
/// na venda — chegam juntos (`group`): um evento só na lista, com as duas alterações nos detalhes.
function AuditEvent({ entry, timeZone }: { entry: AuditEntry; timeZone: string }) {
  const [open, setOpen] = useState(false);
  const group = entry.group ?? null;

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition hover:bg-content/[.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/60 sm:px-5"
      >
        <span
          aria-hidden="true"
          className={`mt-1.5 size-2 shrink-0 rounded-full ${toneDots[auditTone(entry.action)]}`}
        />
        <span className="min-w-0 flex-1">
          {/* A data ao lado do relato só cabe quando há largura para os dois: em 320px ela é larga
              e não quebra, e sobravam uns setenta pixels para o relato — que descia palavra por
              palavra, uma por linha. No celular ela vira a primeira linha, discreta; do `sm` para
              cima volta para a direita, na mesma linha de quem fez o quê. */}
          <span className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
            <span className="shrink-0 whitespace-nowrap text-xs text-content-faint sm:order-2">
              {formatDateTime(entry.occurredAt, timeZone)}
            </span>
            {group ? (
              <span className="min-w-0 text-sm sm:order-1">
                <strong className="font-semibold">{entry.actorName}</strong>{' '}
                <span aria-hidden="true" className="text-content-faint">
                  ·
                </span>{' '}
                <span className="text-content-secondary">{group.title}</span>
              </span>
            ) : (
              <span className="min-w-0 text-sm sm:order-1">
                <strong className="font-semibold">{entry.actorName}</strong>{' '}
                <span className="text-content-secondary">{entry.label.toLowerCase()}</span>{' '}
                <span className="text-content-faint">em {entry.entityLabel.toLowerCase()}</span>
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-sm text-content-secondary">
            {group ? group.explanation : entry.summary}
          </span>
          {/* Agrupado, o que mudou em cada registro vai para os detalhes: a linha já diz o que a
              operação fez, e duas listas de campo nela repetiriam a mesma notícia. */}
          {!group && entry.changes.length ? <Changes entry={entry} className="mt-1.5" /> : null}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`mt-1 size-3.5 shrink-0 text-content-faint transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? <AuditDetails entry={entry} /> : null}
    </li>
  );
}

function AuditDetails({ entry }: { entry: AuditEntry }) {
  const group = entry.group ?? null;
  return (
    <div className="px-4 pb-4 pl-9 sm:px-5 sm:pl-10">
      {group ? (
        <div className="mb-2 space-y-2 rounded-xl border border-surface-border bg-surface-elevated/60 p-3">
          <p className="text-xs text-content-faint">Alterações registradas</p>
          {[entry, ...group.related].map((record) => (
            <div key={record.id}>
              <p className="text-xs font-semibold text-content-secondary">{record.entityLabel}</p>
              <p className="text-xs text-content-faint">{record.summary}</p>
              {record.changes.length ? <Changes entry={record} className="mt-1" /> : null}
            </div>
          ))}
        </div>
      ) : null}
      <dl className="grid gap-x-6 gap-y-1 rounded-xl border border-surface-border bg-surface-elevated/60 p-3 text-xs sm:grid-cols-2">
        <Detail term="Tela" value={entry.entityLabel} />
        <Detail term="Origem" value={originLabels[entry.origin] ?? entry.origin} />
        <Detail term="Contexto" value={contextLabels[entry.contextKind ?? ''] ?? null} />
        <Detail term="E-mail" value={entry.actorEmail} />
        <Detail term="Registro" value={entry.entityId} mono />
        {group?.related.map((record) => (
          <Detail
            key={record.id}
            term={`Registro (${record.entityLabel.toLowerCase()})`}
            value={record.entityId}
            mono
          />
        ))}
        <Detail term="Requisição" value={entry.requestId} mono />
        <Detail term="IP" value={entry.ipAddress} mono />
      </dl>
      {/* O registro cru, como o sistema anterior mostrava. A linha traduzida responde quase
              sempre; quando não responde — evento que guardou o corpo enviado, campo sem nome em
              português — é aqui que está o original. */}
      {entry.details ? (
        <div className="mt-2 rounded-xl border border-surface-border bg-surface-elevated/60 p-3">
          <p className="mb-1.5 text-xs text-content-faint">Dados registrados</p>
          <pre className="max-h-72 overflow-auto font-mono text-[11px] leading-relaxed text-content-secondary">
            {entry.details}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

/// O antes e o depois de cada campo, com as situações traduzidas pela tela do registro.
function Changes({ entry, className }: { entry: AuditEntry; className: string }) {
  return (
    <span className={`block space-y-0.5 ${className}`}>
      {entry.changes.map((change) => (
        <span key={change.field} className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
          <span className="text-content-faint">{change.label}:</span>
          <span className="text-content-faint line-through">
            {formatAuditValue(change.field, change.from, entry.entityType)}
          </span>
          <span aria-hidden="true" className="text-content-faint">
            →
          </span>
          <span className="sr-only">passou a</span>
          <span className="font-medium text-content">
            {formatAuditValue(change.field, change.to, entry.entityType)}
          </span>
        </span>
      ))}
    </span>
  );
}

const contextLabels: Record<string, string> = {
  PLATFORM: 'Plataforma',
  TENANT_MEMBER: 'Empresa',
  TENANT_CUSTOMER: 'Portal do cliente',
  PLATFORM_ACTING_ON_TENANT: 'Suporte atuando na empresa',
};

function Detail({
  term,
  value,
  mono = false,
}: {
  term: string;
  value: string | null;
  mono?: boolean;
}) {
  if (value === null || value === '') return null;
  return (
    <div className="flex gap-1.5">
      <dt className="text-content-faint">{term}:</dt>
      <dd className={`min-w-0 break-all text-content-secondary ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
